import type { EmbedConfig } from "../../search/rules/params.js";
import { allowlistMode, allowlistUnrestricted } from "./safetyRules.js";

/**
 * capPermits answers whether the organization's enforced overlay cap permits a slug, read the
 * way the tile API reads it ({@link allowlistMode} under `"cap"`). An absent cap permits every
 * slug, as an absent safety rule restricts nothing.
 */
const capPermits = (cap: readonly string[] | undefined, slug: string): boolean => {
  if (cap === undefined) {
    return true;
  }

  const mode = allowlistMode(cap, "cap");

  return mode === "all" || (mode === "specific" && cap.includes(slug));
};

/**
 * clampConfigToAllowlists holds a resolved embed config's overlays and terrain to what the
 * organization's enforced overlay cap and the embed's own display lists permit, so neither a URL
 * param, a default_prop nor a host override surfaces a layer whose tiles the tile API refuses.
 *
 * - Overlay: the active overlay is dropped (set to null) when the cap does not permit it, or when
 *   the display list `withOverlays` is restricted (a non-empty list with no `"*"`, per
 *   {@link allowlistUnrestricted}) and leaves it out. A null active overlay is nothing to clamp.
 * - Enrichments: each slug is dropped on the same terms as the active overlay, since both lists
 *   name enrichments and classic overlays alike. The array keeps its reference when nothing is
 *   removed.
 * - Display list: under a cap that names specific slugs, an unrestricted display list becomes the
 *   cap's slugs and a restricted one its intersection with the cap, so the overlays panel offers
 *   no overlay the cap refuses. When nothing is left (a cap that refuses every overlay, or one that
 *   shares no slug with the display list), the list is left as it stands: an empty display list
 *   reads as the whole catalog, so the refusal cannot be written into it, and the picker is hidden
 *   where the control-visibility configuration is read from this one with the same cap
 *   ({@link layersControlOffered}). `withOverlaysControl`, the overlay time slider, is never
 *   touched here: which overlays draw and which instants they draw at are two rules.
 * - Terrain: when the display list `withTerrains` is restricted and the active terrain is not on
 *   it, the terrain falls back to the first slug it names rather than dropping, since the base map
 *   always needs a terrain. The organization states no cap on terrains, so this is a display rule.
 *
 * Returns the SAME reference when nothing changed, so a clamped config stays referentially stable
 * when nothing bites (the surface memoizes on the config).
 *
 * @param config The resolved embed config.
 * @param cap The organization's enforced overlay cap, `safety_rules.with_overlays`, or undefined
 *   when it states none.
 */
export const clampConfigToAllowlists = (
  config: EmbedConfig,
  cap?: readonly string[] | undefined,
): EmbedConfig => {
  const displayUnrestricted = allowlistUnrestricted(config.withOverlays);
  const permits = (slug: string): boolean =>
    capPermits(cap, slug) && (displayUnrestricted || config.withOverlays.includes(slug));

  let next = config;

  if (next.overlay !== null && !permits(next.overlay)) {
    next = { ...next, overlay: null };
  }

  if (next.enrichments.length > 0) {
    const allowedEnrichments = next.enrichments.filter(permits);
    if (allowedEnrichments.length !== next.enrichments.length) {
      next = { ...next, enrichments: allowedEnrichments };
    }
  }

  const capMode = cap === undefined ? "all" : allowlistMode(cap, "cap");
  if (capMode !== "all") {
    const offered = displayUnrestricted
      ? (cap ?? [])
      : config.withOverlays.filter((slug) => capPermits(cap, slug));

    if (
      offered.length > 0 &&
      (displayUnrestricted || offered.length !== config.withOverlays.length)
    ) {
      next = { ...next, withOverlays: [...offered] };
    }
  }

  if (!allowlistUnrestricted(next.withTerrains) && !next.withTerrains.includes(next.terrain)) {
    const fallbackTerrain = next.withTerrains[0];
    if (fallbackTerrain !== undefined) {
      next = { ...next, terrain: fallbackTerrain };
    }
  }

  return next;
};

/**
 * overlaysOffered answers whether the overlay picker has an overlay to offer: whether an embed's
 * display list, read inside the organization's enforced cap, leaves at least one overlay whose
 * tiles the tile API draws.
 *
 * An absent cap restricts nothing, and a cap holding the wildcard admits the whole catalog, so
 * either offers what the display list offers, which is always something: an empty display list
 * reads as the whole catalog. A cap that is empty admits nothing. A cap naming specific slugs
 * offers them where the display list is unrestricted, and the slugs the two share where it is not.
 *
 * @param withOverlays The display list, an embed's `with_overlays`.
 * @param cap The organization's enforced overlay cap, `safety_rules.with_overlays`, or undefined
 *   when it states none.
 */
export const overlaysOffered = (
  withOverlays: readonly string[],
  cap?: readonly string[] | undefined,
): boolean => {
  const capMode = cap === undefined ? "all" : allowlistMode(cap, "cap");
  if (capMode !== "specific") {
    return capMode === "all";
  }

  return allowlistUnrestricted(withOverlays) || withOverlays.some((slug) => capPermits(cap, slug));
};

/**
 * layersControlOffered answers whether the layers control shows: the button that opens the panel
 * switching terrains and overlays. It shows where the overlay picker has an overlay to offer
 * ({@link overlaysOffered}), and where the terrain list names the terrains to switch between; an
 * unrestricted terrain list raises nothing by itself.
 *
 * `withOverlaysControl` takes no part in it. That field is the overlay time slider, and an embed
 * whose overlays draw only at the present instant still offers every overlay its cap admits.
 *
 * @param config The display lists of a resolved embed config.
 * @param cap The organization's enforced overlay cap, `safety_rules.with_overlays`, or undefined
 *   when it states none.
 *
 * @example
 * ```ts
 * import { layersControlOffered } from "@mountaya/studio";
 *
 * layersControlOffered({ withOverlays: [], withTerrains: [] }, ["*"]); // true
 * layersControlOffered({ withOverlays: [], withTerrains: [] }, []); // false
 * layersControlOffered({ withOverlays: [], withTerrains: ["topo", "satellite"] }, []); // true
 * ```
 */
export const layersControlOffered = (
  config: Pick<EmbedConfig, "withOverlays" | "withTerrains">,
  cap?: readonly string[] | undefined,
): boolean =>
  overlaysOffered(config.withOverlays, cap) || !allowlistUnrestricted(config.withTerrains);
